<script>
    import { onMount } from "svelte";
    import { isMobile } from "../../../stores";

    import {
        SvelteTable,
        ErrorModal,
        PageHead,
        PageLoading,
        SearchInput,
        Button,
        NotificationModal,
        CardLayout,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../../Components/Common/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getAmountInCurrency,
        getFormatDate,
        getNumber,
        getText,
        getUrlWithoutSearch,
        replaceURL,
        showError,
        showException,
        syncDefaultDoctorFilter,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import ReconcileEftCheckbox from "./ReconcileEftCheckbox.svelte";
    import ERAsEncountersColumn from "./ERAsEncountersColumn.svelte";

    export let params = {};
    export let toggleShowERAsTable = () => {};
    export let selectedReconciliationEraID = Number(0);

    let isLoadingSelectedReconciliationERA = false,
        isLoadingReconciliationEFT = false,
        selectedReconciliationEra = {},
        returnedEftList = [],
        isReconcileModalOpen = false,
        isReconcileNotificationModalOpen = false,
        isReconciling = false;

    let selectedEftsIdArr = [],
        selectedEfts = [],
        checkEFTsString = "";

    let targetAmount = 0,
        sumAmountOfSelectedReceipts = 0;

    let filters = {};
    let selectedDoctors = [];
    let hideAccountColumn = false; // hide doctor filter when all EFTs have accountId == 0

    $: isLoading =
        isLoadingSelectedReconciliationERA || isLoadingReconciliationEFT;

    $: displaySumAmount =
        sumAmountOfSelectedReceipts >= 0
            ? getAmountInCurrency(sumAmountOfSelectedReceipts)
            : getAmountInCurrency(0);

    $: isTargetAmountMatched = targetAmount == displaySumAmount;

    const columns = [
        {
            key: "id",
            title: "ID",
            headerClass:
                "text-left px-3 w-[5%] cursor-default ml-3 rounded-tl-xl",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "eraCreatedDate",
            title: "ERA Created Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.eraCreatedDate),
        },
        {
            key: "encounterDetails",
            title: "Encounters",
            headerClass: "text-left px-3 w-[30%] cursor-default ml-3",
            class: "text-left px-3 w-[30%] cursor-default",
            renderComponent: {
                component: ERAsEncountersColumn,
            },
        },
        {
            key: "reconciledDate",
            title: "Reconciled Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.reconciledDate),
        },
        {
            key: "reconciledBy",
            title: "Reconciled By",
            headerClass: "text-left px-3 w-[30%] cursor-default ml-3",
            class: "text-left px-3 w-[30%] cursor-default",
            renderValue: (v) => _renderUsers(v.userDetail),
        },
        {
            key: "eraAmount",
            title: "Amount",
            headerClass: "text-left px-3 w-[5%] cursor-default ml-3",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getAmountInCurrency(v.eraAmount),
        },
        {
            key: "eraPayer",
            title: "Payer",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getText(v.eraPayer),
        },
        {
            key: "eraPayerDate",
            title: "Paid Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.eraPayerDate),
        },
        {
            key: "eraReference",
            title: "Reference",
            headerClass: "text-left px-3 w-[13%] cursor-default ml-3",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.eraReference),
        },
        {
            key: "eraTransactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 w-[13%] cursor-default ml-3",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.eraTransactionId),
        },
        {
            key: "eraLogTransactionId",
            title: "Log Transaction ID",
            headerClass: "text-left w-[13%] cursor-default px-3 ml-3",
            class: "text-left w-[13%] cursor-default px-3",
            renderValue: (v) => getText(v.eraLogTransactionId),
        },
        {
            key: "notes",
            title: "Notes",
            headerClass: "text-left px-3 w-[13%] cursor-default rounded-tr-xl",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.notes),
        },
    ];

    const eftColumns = [
        {
            key: "checkbox",
            title: "",
            headerClass: "text-left px-3 w-[5%] cursor-default rounded-tl-xl",
            class: "text-left px-3 w-[5%] cursor-default cursor-default",
            renderComponent: {
                component: ReconcileEftCheckbox,
                props: {
                    onToggleSelect: (row) => _updateSelectEftsArr(row),
                },
            },
        },
        {
            key: "id",
            title: "ID",
            headerClass: "text-left px-3 w-[5%] cursor-default ml-3",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "transactionDate",
            title: "Date Received",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.transactionDate),
        },
        {
            key: "amount",
            title: "Amount",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getAmountInCurrency(v.amount),
        },
        {
            key: "transactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 w-[20%] cursor-default ml-3",
            class: "text-left px-3 w-[20%] cursor-default",
            renderValue: (v) => getText(v.transactionId),
        },
        {
            key: "description",
            title: "Description",
            headerClass:
                "text-left px-3 w-[50%] cursor-default ml-3 rounded-tr-xl",
            class: "text-left px-3 w-[50%] cursor-default",
            renderValue: (v) => getText(v.description),
        },
    ];

    onMount(async () => {
        getUrlWithoutSearch();

        if (selectedReconciliationEraID) {
            if (params.query) updateSearchParamsByQuery(params.query);

            filters = await syncDefaultDoctorFilter(filters, "doctorId");

            await loadSelectedReconciliationEra(selectedReconciliationEraID);

            await loadReconciliationEFT();
        } else toggleShowERAsTable();
    });

    const loadSelectedReconciliationEra = async (id) => {
        isLoadingSelectedReconciliationERA = true;

        await apiMethods.getMethod
            .getReconciliationERAbyId(id)
            .then(async (res) => {
                _handleReconciliationDetail(res);

                // Auto-populate doctorId filter from the first encounter's doctor
                if (res.encounters && res.encounters.length > 0 && !filters.doctorId) {
                    const patientEncounterId = res.encounters[0].patientEncounterId;

                    if (patientEncounterId) {
                        await apiMethods.getMethod
                            .getPatientEncounterById(patientEncounterId)
                            .then((encounterRes) => {
                                if (encounterRes && encounterRes.doctorId) {
                                    filters.doctorId = encounterRes.doctorId.toString();
                                    selectedDoctors = [encounterRes.doctorId];
                                }
                            })
                            .catch((err) => {
                                // Silently fail - user can still manually select doctor
                                console.warn("Could not load encounter doctor:", err);
                            });
                    }
                }
            })
            .catch((err) =>
                showException(
                    "Unable to load selected ERA details, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isLoadingSelectedReconciliationERA = false;
    };

    const loadReconciliationEFT = async () => {
        isLoadingReconciliationEFT = true;

        const searchParams = {
            "filter[reconciled]": 0,
        };

        if (
            selectedReconciliationEra &&
            selectedReconciliationEra.eraCreatedDate
        )
            searchParams["filter[transactionDateFrom]"] =
                selectedReconciliationEra.eraCreatedDate?.split("T")[0];

        if (filters.search) searchParams.search = filters.search;

        if (filters.doctorId) {
            searchParams["filter[doctorId]"] = filters.doctorId;
        }

        if (selectedEftsIdArr.length)
            await apiMethods.getMethod
                .getReconciliationEFT(0, 999999, {
                    "filter[id]": selectedEftsIdArr.join(","),
                })
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to load previously selected ERAs, please refresh and try again or contact support...",
                        );
                    else selectedEfts = [...res.results];
                })
                .catch((err) => showError(err));

        await apiMethods.getMethod
            .getReconciliationEFT(0, 999999, searchParams)
            .then((res) => _handleReconciliationEFT(res))
            .catch((err) =>
                showException(
                    "Unable to load Reconciliation EFT details, please refresh the page and try again and contact support if the issue persists.",
                    toggleShowERAsTable,
                ),
            );

        isLoadingReconciliationEFT = false;
    };

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    filters.page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    filters.pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("reconciled="))
                    filters.reconciled = Number(arr[i].split("=")[1]);

                if (arr[i].includes("eraSearchStr="))
                    filters.search = arr[i].split("=")[1];

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("checkEFTs=")) {
                    checkEFTsString = arr[i].split("=")[1];
                    selectedEftsIdArr = checkEFTsString.split(",");
                }

                if (arr[i].includes("checkPrice="))
                    sumAmountOfSelectedReceipts = Number(arr[i].split("=")[1]);
            }
            updateURL();
        }
    };

    const updateURL = () => {
        if (!checkEFTsString)
            replaceURL(`/Reconciliations/tab=ERAs${_getParamsString()}`);
        else
            replaceURL(
                `/Reconciliations/tab=ERAs${_getParamsString()}&checkEFTs=${checkEFTsString}&checkPrice=${getNumber(
                    sumAmountOfSelectedReceipts,
                    2,
                )}`,
            );
    };

    const confirmReconciliation = async () => {
        isReconciling = true;

        await apiMethods.putMethod
            .updateReconciliationERA({
                id: selectedReconciliationEraID,
                eftReconciliationIds: [...selectedEftsIdArr],
            })
            .then((res) => {
                if (res.error) showError(res.error);
                else {
                    selectedEftsIdArr = [];
                    checkEFTsString = "";
                    toggleShowERAsTable();
                }
            });

        _closeReconcileModal();

        isReconciling = false;
    };

    const confirmReconcileWithAmountNotMatched = () => {
        _closeReconcileNotificationModal();

        _openReconcileModal();
    };

    const ERAhandleClickCell = (
        { target: { className, id } },
        rowData,
        key,
    ) => {};

    const EFThandleClickCell = ({ target: { className } }, rowData, key) => {};

    const handleSearch = async () => {
        const searchParams = {
            "filter[reconciled]": 0,
        };

        if (
            selectedReconciliationEra &&
            selectedReconciliationEra.eraCreatedDate
        )
            searchParams["filter[transactionDateFrom]"] =
                selectedReconciliationEra.eraCreatedDate?.split("T")[0];

        if (filters.search) searchParams.search = filters.search;

        if (filters.doctorId) {
            searchParams["filter[doctorId]"] = filters.doctorId;
        }

        updateURL();

        await apiMethods.getMethod
            .getReconciliationEFT(0, 999999, searchParams)
            .then((res) => _handleReconciliationEFT(res))
            .catch((err) =>
                showException(
                    "Unable to load Reconciliation EFT details, please refresh the page and try again and contact support if the issue persists.",
                    toggleShowERAsTable,
                ),
            );
    };

    const handleSelectDoctor = async () => {
        filters.doctorId = selectedDoctors.join(",");
        await loadReconciliationEFT();
    };

    const clearFilterDoctor = async () => {
        selectedDoctors = [];
        delete filters.doctorId;
        await loadReconciliationEFT();
    };

    // HELPER FUNCTIONS
    const _handleReconciliationDetail = async (res) => {
        // res = {
        //     id: 1,
        //     source: "string",
        //     createdDate: "2023-08-16T00:09:26.082Z",
        //     updatedDate: "2023-08-16T00:09:26.082Z",
        //     deletedDate: "2023-08-16T00:09:26.082Z",
        //     error: "",
        //     eraTransactionId: "string",
        //     eraLogTransactionId: "string",
        //     eraCreatedDate: "2023-08-16T00:09:26.082Z",
        //     eraAmount: 123,
        //     eraReference: "string",
        //     eraPayer: "string",
        //     eraPayerDate: "2023-08-16T00:09:26.082Z",
        //     reconciled: false,
        //     reconciledBy: 1,
        //     reconciledDate: "2023-08-16T00:09:26.082Z",
        //     notes: "string",
        //     eftReconciliationIds: [1],
        //     encounters: [
        //         {
        //             id: 1,
        //             reconciliationERAId: 1,
        //             patientEncounterId: 1,
        //             providerNumer: "string",
        //             amount: 123,
        //         },
        //     ],
        // };

        if (res.id) {
            selectedReconciliationEra = { ...res };

            await _getUserName();

            targetAmount = getAmountInCurrency(res.eraAmount);

            const searchArr = [];

            if (res.eraPayer) searchArr.push(res.eraPayer);
            if (res.eraTransactionId) searchArr.push(res.eraTransactionId);

            if (!filters.search) filters.search = "";
        } else if (res.error) showError(res.error, toggleShowERAsTable);
        else
            showError(
                "Unable to load selected ERA details, please refresh the page and try again and contact support if the issue persists.",
                toggleShowERAsTable,
            );
    };

    const _getUserName = async () => {
        if (!selectedReconciliationEra.reconciledBy) return;

        await apiMethods.getMethod
            .getUserById(Math.abs(selectedReconciliationEra.reconciledBy))
            .then((res) => {
                if (res.id)
                    selectedReconciliationEra.userDetail = {
                        name: res.fullName || "",
                        email: res.email || "",
                    };
            });
    };

    const _handleReconciliationEFT = (res) => {
        // res = {
        //     start: 0,
        //     limit: 25,
        //     total: 1,
        //     error: "string",
        //     results: [
        //         {
        //             id: 1,
        //             source: "string",
        //             createdDate: "2023-07-21T01:57:36.150Z",
        //             updatedDate: "2023-07-21T01:57:36.150Z",
        //             deletedDate: "2023-07-21T01:57:36.150Z",
        //             error: "string",
        //             transactionId: "string",
        //             transactionDate: "2023-07-21T01:57:36.150Z",
        //             description: "string",
        //             amount: 5555,
        //             reconciled: false,
        //             paymentId: 1,
        //             eraReconciliationId: 11,
        //         },
        //         {
        //             id: 2,
        //             source: "string",
        //             createdDate: "2023-07-21T01:57:36.150Z",
        //             updatedDate: "2023-07-21T01:57:36.150Z",
        //             deletedDate: "2023-07-21T01:57:36.150Z",
        //             error: "string",
        //             transactionId: "string",
        //             transactionDate: "2023-07-21T01:57:36.150Z",
        //             description: "string",
        //             amount: 5555,
        //             reconciled: false,
        //             paymentId: 1,
        //             eraReconciliationId: 22,
        //         },
        //     ],
        // };

        if (res.results && res.results.length)
            returnedEftList = [...res.results];
        else if (res.error) showError(res.error);
        else returnedEftList = [];

        if (returnedEftList && returnedEftList.length)
            returnedEftList.map((eft) => (eft.checked = false));

        if (selectedEfts.length) {
            if (!returnedEftList.length) returnedEftList = [...selectedEfts];
            else
                selectedEfts.map((eft) => {
                    if (!returnedEftList.some((rEft) => rEft.id == eft.id))
                        returnedEftList.unshift(eft);
                });
        }

        if (checkEFTsString) {
            selectedEftsIdArr = checkEFTsString
                .split(",")
                .map((eft) => (eft = Number(eft)));

            if (returnedEftList && returnedEftList.length)
                returnedEftList.map((eft) => {
                    if (selectedEftsIdArr.includes(eft.id)) eft.checked = true;
                });
        }

        _determineAccountColumnVisibility(returnedEftList);
    };

    const _determineAccountColumnVisibility = (results) => {
        if (!results || !results.length) {
            hideAccountColumn = false;
            return;
        }

        if (results.length == 1) {
            hideAccountColumn = results[0].accountId == 0;
            return;
        }

        hideAccountColumn = !results.some((res) => res.accountId != 0);
    };

    const _openReconcileModal = () => (isReconcileModalOpen = true);

    const _closeReconcileModal = () => (isReconcileModalOpen = false);

    const _openReconcileNotificationModal = () =>
        (isReconcileNotificationModalOpen = true);

    const _closeReconcileNotificationModal = () =>
        (isReconcileNotificationModalOpen = false);

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.page || params.page == 0) str += `&page=${params.page}`;

            if (params.pageSize) str += `&size=${params.pageSize}`;

            if (params["filter[eraSearch]"])
                str += `&eraSearchStr=${params["filter[eraSearch]"]}`;

            if (params["filter[doctorId]"])
                str += `&doctorId=${params["filter[doctorId]"]}`;

            if (params.selectedReconciliationEraID)
                str += `&selectedReconciliationEraID=${params.selectedReconciliationEraID}`;
        }

        return encodeSearchParams(str);
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.page || filters.page == 0) params.page = filters.page;

        if (filters.pageSize) params.pageSize = filters.pageSize;

        if (filters.search) params["filter[eraSearch]"] = filters.search;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        if (selectedReconciliationEraID)
            params.selectedReconciliationEraID = selectedReconciliationEraID;

        return params;
    };

    const _renderUsers = (userDetail) => {
        if (!userDetail) return "";

        if (!userDetail.name)
            if (!userDetail.email) return "";
            else return getText(userDetail.email);

        if (!userDetail.email) return getText(userDetail.name);

        return `${getText(userDetail.name)} (${getText(userDetail.email)})`;
    };

    const _updateSelectEftsArr = (row) => {
        row.checked = !row.checked;

        const { amount } = row;

        if (selectedEftsIdArr.includes(row.id)) {
            selectedEftsIdArr = selectedEftsIdArr.filter(
                (eftId) => eftId != row.id,
            );
            selectedEfts = selectedEfts.filter((eft) => eft.id != row.id);
            sumAmountOfSelectedReceipts -= amount;
        } else {
            selectedEftsIdArr = [...selectedEftsIdArr, row.id];
            selectedEfts = [...selectedEfts, row];
            sumAmountOfSelectedReceipts += amount;
        }

        checkEFTsString = _getCheckboxString(selectedEftsIdArr);

        updateURL();
    };

    const _getCheckboxString = (selectedEftsIdArr) => {
        if (!selectedEftsIdArr.length) return "";

        return selectedEftsIdArr.join();
    };
</script>

{#if isLoading}
    <PageLoading />
{:else}
    <div class="flex flex-col flex-1 gap-y-4 relative">
        <PageHead
            title="Reconcile the following ERA"
            backFunc={toggleShowERAsTable}
        />

        <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-12">
            <CardLayout title="ERA">
                {#if $isMobile}
                    <div class="h-auto w-minus48">
                        <div class="overflow-x-auto overflow-y-visible">
                            <SvelteTable
                                {columns}
                                rows={[{ ...selectedReconciliationEra }]}
                                clickCellFunc={({
                                    detail: { event, row, key },
                                }) => {
                                    ERAhandleClickCell(event, row, key);
                                }}
                            />
                        </div>
                    </div>
                {:else}
                    <SvelteTable
                        {columns}
                        rows={[{ ...selectedReconciliationEra }]}
                        clickCellFunc={({ detail: { event, row, key } }) => {
                            ERAhandleClickCell(event, row, key);
                        }}
                    />
                {/if}
            </CardLayout>

            <div class="flex items-center justify-between w-full">
                <div class="flex gap-x-4 items-end">
                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleSearch}
                        inputClass={`${$isMobile ? "w-72" : "w-96"}`}
                    />

                    {#if !hideAccountColumn}
                        <DefaultDoctorFilter
                            bind:filter={filters}
                            filterPropertyName="doctorId"
                            onChangeFunc={async () => {
                                selectedDoctors = filters["doctorId"].split(",").map(id => Number(id));
                                await loadReconciliationEFT();
                            }}
                        />

                        <DoctorMultiSearchSelect
                            bind:searchResult={selectedDoctors}
                            onChangeFunc={handleSelectDoctor}
                            inputClass="w-32"
                            zIndex="3"
                        />
                    {/if}

                    {#if !$isMobile}
                        <span class="text-lg">
                            Selected EFT Amount:
                            <em
                                class={sumAmountOfSelectedReceipts > 0
                                    ? targetAmount != displaySumAmount
                                        ? "text-error"
                                        : "text-secondary"
                                    : ""}
                            >
                                {displaySumAmount}
                            </em>
                        </span>
                    {/if}
                </div>

                <Button
                    clickFunc={isTargetAmountMatched
                        ? _openReconcileModal
                        : _openReconcileNotificationModal}
                    isDisabled={!selectedEftsIdArr.length}
                >
                    Reconcile
                </Button>
            </div>

            {#if $isMobile}
                <span class="text-lg">
                    Selected EFT Amount:
                    <em
                        class={sumAmountOfSelectedReceipts > 0
                            ? targetAmount != displaySumAmount
                                ? "text-error"
                                : "text-secondary"
                            : ""}
                    >
                        {displaySumAmount}
                    </em>
                </span>
            {/if}

            {#if !returnedEftList.length}
                <div>No Available EFTs</div>
            {:else}
                <CardLayout title="Available EFTs">
                    {#if $isMobile}
                        <div class="h-auto w-minus48">
                            <div class="overflow-x-auto overflow-y-visible">
                                <SvelteTable
                                    columns={eftColumns}
                                    rows={returnedEftList}
                                    clickCellFunc={({
                                        detail: { event, row, key },
                                    }) => {
                                        EFThandleClickCell(event, row, key);
                                    }}
                                />
                            </div>
                        </div>
                    {:else}
                        <SvelteTable
                            columns={eftColumns}
                            rows={returnedEftList}
                            clickCellFunc={({
                                detail: { event, row, key },
                            }) => {
                                EFThandleClickCell(event, row, key);
                            }}
                        />
                    {/if}
                </CardLayout>
            {/if}
        </div>
    </div>
{/if}

<NotificationModal
    isModalOpen={isReconcileModalOpen}
    isConfirming={isReconciling}
    closeModal={_closeReconcileModal}
    confirmAction={confirmReconciliation}
    submitText={isReconciling ? "Reconcile" : "Confirm"}
    closeModalFunction={_closeReconcileModal}
>
    <p class="leading-6 text-error">
        Reconcile this ERA with selected EFT IDs:
    </p>

    <p class="leading-6">{selectedEftsIdArr.join()}</p>
</NotificationModal>

<NotificationModal
    title="Warning"
    submitText="Continue"
    cancelText="Cancel"
    isModalOpen={isReconcileNotificationModalOpen}
    closeModal={_closeReconcileNotificationModal}
    confirmAction={confirmReconcileWithAmountNotMatched}
    closeModalFunction={_closeReconcileNotificationModal}
>
    <p class="leading-6">
        You are attempting to reconcile <em>{displaySumAmount}</em> against an
        ERA transfer of <em>{targetAmount}</em>. Are you sure you want to
        reconcile these amounts? This will amount to an {getNumber(
            Number(
                targetAmount.toString().replaceAll("$", "").replaceAll(",", ""),
            ),
            2,
        ) >
        getNumber(
            Number(
                displaySumAmount
                    .toString()
                    .replaceAll("$", "")
                    .replaceAll(",", ""),
            ),
            2,
        )
            ? "under"
            : "over"} payment.
    </p>

    <p class="leading-6">Do you want to continue?</p>
</NotificationModal>
